import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Button, Card, Container, Grid, IconButton, Stack, SwipeableDrawer, TextField, Typography } from '@mui/material';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { addAssetCategories, getAssetCategories, updateAssetCategories } from '../../../redux/slices/assetsRedux/assetCategoriesRedux';
import { accessVerify, capitalize } from '../../../utils/common';
import PermissionRequired from '../../errorPages/permissionRequired';

export default function AssetCategories() {
  const dataModel = {
    id: '',
    name: '',
    description: '',
    mainCategory: '',
  };
  const [selectedDataObj, setSelectedDataObj] = useState(dataModel);
  const { themeStretch } = useSettings();
  const [isAdd, setIsAdd] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [assetCategoriesList, setAssetCategoriesList] = useState([]);
  const { data, addData } = useSelector((state) => state.assetCategories);
  const [isCustomField, setIsCustomField] = useState(false);
  const [cashFlowMainCategoryTypes, setCashFlowMainCategoryTypes] = useState([]);

  useEffect(() => {
    dispatch(getAssetCategories());
  }, []);

  useEffect(() => {
    setAssetCategoriesList(data);

    const uniqueCategories = [...new Set(data.map((item) => item.mainCategory))];

    setCashFlowMainCategoryTypes(
      uniqueCategories.map((category) => ({
        label: category,
        value: category,
      }))
    );
    setIsModelOpen(false);
  }, [data]);

  const manageModel = (modelData, type) => {
    if (isModelOpen) {
      setIsModelOpen(false);
    } else {
      setIsModelOpen(true);
      setIsAdd(type === 'add');
      if (type === 'add') {
        setSelectedDataObj(dataModel);
      } else {
        setSelectedDataObj(modelData);
      }
      setIsCustomField(false);
    }
  };

  useEffect(() => {
    if (addData?.data) {
      dispatch(getAssetCategories());
    }
  }, [addData]);

  const updateEditingData = (key, value) => {
    setSelectedDataObj({
      ...selectedDataObj,
      [key]: value,
    });
  };

  const columns = !assetCategoriesList?.[0]
    ? []
    : Object.keys(assetCategoriesList?.[0])
        .map((value) => {
          if (value === 'locationData' || value === 'userData') {
            return {};
          } else {
            return {
              accessorKey: value,
              header: capitalize(value),
            };
          }
        })
        ?.filter((value) => value?.accessorKey);

  const [rowSelection, setRowSelection] = useState(false);

  const handleSubmit = () => {
    if (selectedDataObj.name.length === 0) {
      toast.error('Asset name is required!');
    } else if (selectedDataObj.description.length === 0) {
      toast.error('Asset description is required!');
    } else {
      dispatch(isAdd ? addAssetCategories(selectedDataObj) : updateAssetCategories(selectedDataObj));
    }
  };
  return (
    <Page title={`Asset Categories`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`Asset Categories`}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            {
              name: 'Asset',
              href: '',
            },
            { name: `Asset Categories` },
          ]}
          action={
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:plus-fill' />}
              onClick={() => {
                manageModel(dataModel, 'add');
              }}
            >
              New Asset Category
            </Button>
          }
        />

        {accessVerify('expense_VIEW') ? (
          <DataGridTable
            name={'Asset Category List'}
            data={assetCategoriesList}
            column={columns}
            onRowClick={(row) => {
              manageModel(row?.original, 'edit');
            }}
            isRowClickable={true}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowSelection={false}
            enableRowActions={false}
            renderRowActionItems={(value, closeMenu) => []}
          />
        ) : (
          <PermissionRequired />
        )}

        <SwipeableDrawer
          anchor={'top'}
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => setIsModelOpen(false)}
          PaperProps={{
            sx: {
              width: '90%',
              height: '90%',
              marginLeft: '5%',
              marginTop: '3%',
              borderRadius: '10px',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <h1>{isAdd ? 'Add' : 'Edit'} Asset Category</h1>
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12} md={8}>
              <Stack spacing={3} sx={{ p: 3 }}>
                <Card sx={{ p: 3 }}>
                  <Typography variant='subtitle1' padding='10px 0'>
                    Asset Category Information
                  </Typography>
                  <Stack spacing={3}>
                    {isCustomField ? (
                      <Button
                        color={'primary'}
                        variant='contained'
                        onClick={() => {
                          setIsCustomField(false);
                        }}
                      >
                        Use Already Entered Main Category One
                      </Button>
                    ) : (
                      <Button
                        color={'primary'}
                        variant='contained'
                        onClick={() => {
                          setIsCustomField(true);
                        }}
                      >
                        Continue with New Main Category
                      </Button>
                    )}
                    {!isCustomField ? (
                      <Autocomplete
                        onChange={(event, newValue) => {
                          updateEditingData('mainCategory', newValue?.value);
                        }}
                        options={cashFlowMainCategoryTypes || []}
                        value={cashFlowMainCategoryTypes?.find((value) => value?.value === selectedDataObj?.mainCategory) ?? null}
                        renderInput={(params) => <TextField label='Main Category' {...params} />}
                        getOptionLabel={(option) => option.label || ''}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                      />
                    ) : (
                      <TextField fullWidth label='Custom Main Category' value={selectedDataObj?.mainCategory} onChange={(e) => updateEditingData('mainCategory', e.target.value)} />
                    )}
                    <TextField fullWidth label='Name' value={selectedDataObj?.name} onChange={(e) => updateEditingData('name', e.target.value)} />
                    <TextField fullWidth label='Description' value={selectedDataObj?.description} onChange={(e) => updateEditingData('description', e.target.value)} />
                  </Stack>
                </Card>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3 }}>
                <Stack spacing={3} sx={{ p: 3 }}>
                  <LoadingButton type='submit' variant='contained' size='large' style={{ width: '100%' }} onClick={handleSubmit}>
                    {isAdd ? 'Add Asset Category' : 'Save Changes'}
                  </LoadingButton>
                  <Button
                    color='info'
                    variant='outlined'
                    size='large'
                    style={{ width: '100%' }}
                    onClick={() => {
                      manageModel(dataModel, 'add');
                    }}
                  >
                    Close
                  </Button>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </SwipeableDrawer>
      </Container>
    </Page>
  );
}
